import { useState, useEffect, useCallback } from 'react';
import { leaveRequestsService, LeaveRequestFilters } from '../services/api/leaveRequests';
import { LeaveRequestDetail, LeaveRequestStatus } from '../types';

export const useLeaveRequests = (filters?: LeaveRequestFilters) => {
  const [requests, setRequests] = useState<LeaveRequestDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(filters?.page || 1);
  const [pageSize, setPageSize] = useState(filters?.pageSize || 20);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await leaveRequestsService.getAll({
        ...filters,
        page,
        pageSize,
      });
      setRequests(response.items);
      setTotal(response.total);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return {
    requests,
    loading,
    error,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    refetch: fetchRequests,
  };
};

export const useMyLeaveRequests = (status?: LeaveRequestStatus) => {
  const [requests, setRequests] = useState<LeaveRequestDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await leaveRequestsService.getMyRequests(
        status,
        page,
        pageSize
      );
      setRequests(response.items);
      setTotal(response.total);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [status, page, pageSize]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return {
    requests,
    loading,
    error,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    refetch: fetchRequests,
  };
};

export const usePendingApprovals = () => {
  const [requests, setRequests] = useState<LeaveRequestDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await leaveRequestsService.getPendingApprovals();
      setRequests(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  return {
    requests,
    loading,
    error,
    refetch: fetchApprovals,
  };
};

export const useLeaveRequest = (id: number | null) => {
  const [request, setRequest] = useState<LeaveRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequest = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await leaveRequestsService.getById(id);
      setRequest(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  return {
    request,
    loading,
    error,
    refetch: fetchRequest,
  };
};
